/** Builds, combines and measures binary selection masks stored in native image pixel coordinates. */
import {Bounds, PixelBuffer, Point} from './imageEditing';

/** One byte per pixel: 1 = selected, 0 = not selected. Never mutated after creation. */
export interface SelectionMask {
   width: number;
   height: number;
   data: Uint8Array;
}

/** How a new selection step combines with the existing selection. */
export enum SelectionMode {
   Replace = 'replace',
   Add = 'add',
   Subtract = 'subtract',
}

/** Picks the combination mode from the modifier keys held during a selection click or drag. */
export const selectionModeFromModifiers = (shiftKey: boolean, altKey: boolean): SelectionMode => {
   if (shiftKey) {
      return SelectionMode.Add;
   }
   return altKey ? SelectionMode.Subtract : SelectionMode.Replace;
};

/** Limits a value to the inclusive range. */
const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

/** Returns the mask, or null ("no selection") when it selects no pixel. */
const emptyToNull = (mask: SelectionMask): SelectionMask | null => mask.data.includes(1) ? mask : null;

/** Selects every pixel touched by the rectangle spanned by two image points, in any drag direction. */
export const createRectangleMask = (width: number, height: number, from: Point, to: Point): SelectionMask => {
   const left = clamp(Math.floor(Math.min(from.x, to.x)), 0, width);
   const right = clamp(Math.ceil(Math.max(from.x, to.x)), 0, width);
   const top = clamp(Math.floor(Math.min(from.y, to.y)), 0, height);
   const bottom = clamp(Math.ceil(Math.max(from.y, to.y)), 0, height);
   const data = new Uint8Array(width * height);

   for (let y = top; y < bottom; y += 1) {
      data.fill(1, y * width + left, y * width + right);
   }
   return {width, height, data};
};

/**
 * Selects the 4-connected area around the seed whose colour stays within `tolerance` of the seed colour.
 * Distance is the largest RGBA channel difference. Comparing against the seed rather than each neighbour
 * stops the fill creeping along soft gradients, and 4-connectivity stops it leaking through one-pixel
 * diagonal gaps. Fully transparent pixels compare on alpha only, so all of them count as one colour.
 */
export const createMagicWandMask = (pixels: PixelBuffer, seed: Point, tolerance: number): SelectionMask => {
   const {width, height, data} = pixels;
   const selected = new Uint8Array(width * height);
   const seedX = Math.floor(seed.x);
   const seedY = Math.floor(seed.y);
   if (seedX < 0 || seedY < 0 || seedX >= width || seedY >= height) {
      return {width, height, data: selected};
   }

   const seedOffset = (seedY * width + seedX) * 4;
   const seedRed = data[seedOffset];
   const seedGreen = data[seedOffset + 1];
   const seedBlue = data[seedOffset + 2];
   const seedAlpha = data[seedOffset + 3];

   /** Reports whether a pixel is not yet selected and close enough to the seed colour. */
   const canFill = (index: number): boolean => {
      if (selected[index]) {
         return false;
      }
      const offset = index * 4;
      const alpha = data[offset + 3];
      if (alpha === 0 || seedAlpha === 0) {
         return Math.abs(alpha - seedAlpha) <= tolerance;
      }
      return Math.max(
         Math.abs(data[offset] - seedRed),
         Math.abs(data[offset + 1] - seedGreen),
         Math.abs(data[offset + 2] - seedBlue),
         Math.abs(alpha - seedAlpha)
      ) <= tolerance;
   };

   // Scanline fill: every stack entry starts a horizontal run, then the rows above and
   // below the run are scanned for new runs (only directly adjacent pixels: 4-connected).
   const stack: number[] = [seedY * width + seedX];
   for (let index = stack.pop(); index !== undefined; index = stack.pop()) {
      if (!canFill(index)) {
         continue;
      }
      const y = Math.floor(index / width);
      const rowStart = y * width;
      let left = index - rowStart;
      let right = left;
      while (left > 0 && canFill(rowStart + left - 1)) {
         left -= 1;
      }
      while (right < width - 1 && canFill(rowStart + right + 1)) {
         right += 1;
      }
      selected.fill(1, rowStart + left, rowStart + right + 1);

      for (const row of [y - 1, y + 1]) {
         if (row < 0 || row >= height) {
            continue;
         }
         const neighbourStart = row * width;
         let inRun = false;
         for (let x = left; x <= right; x += 1) {
            const fillable = canFill(neighbourStart + x);
            if (fillable && !inRun) {
               stack.push(neighbourStart + x);
            }
            inRun = fillable;
         }
      }
   }
   return {width, height, data: selected};
};

/** Combines the selection with a new mask; Add to no selection gives the mask, Subtract from no selection gives none. */
export const combineMasks = (
   base: SelectionMask | null,
   addition: SelectionMask,
   mode: SelectionMode
): SelectionMask | null => {
   if (mode === SelectionMode.Replace) {
      return emptyToNull(addition);
   }
   if (!base) {
      return mode === SelectionMode.Add ? emptyToNull(addition) : null;
   }

   const data = new Uint8Array(base.data.length);
   for (let index = 0; index < data.length; index += 1) {
      data[index] = mode === SelectionMode.Add
         ? base.data[index] | addition.data[index]
         : base.data[index] & (1 - addition.data[index]);
   }
   return emptyToNull({width: base.width, height: base.height, data});
};

/** Swaps selected and unselected pixels; inverting a full selection gives no selection. */
export const invertMask = (mask: SelectionMask): SelectionMask | null =>
   emptyToNull({width: mask.width, height: mask.height, data: mask.data.map(value => 1 - value)});

/** Returns the smallest pixel rectangle (right/bottom exclusive) containing every selected pixel, or null. */
export const getMaskBounds = (mask: SelectionMask): Bounds | null => {
   let left = mask.width;
   let top = mask.height;
   let right = -1;
   let bottom = -1;

   for (let y = 0; y < mask.height; y += 1) {
      for (let x = 0; x < mask.width; x += 1) {
         if (mask.data[y * mask.width + x]) {
            left = Math.min(left, x);
            right = Math.max(right, x);
            top = Math.min(top, y);
            bottom = Math.max(bottom, y);
         }
      }
   }
   return right < 0 ? null : {left, top, right: right + 1, bottom: bottom + 1};
};
